import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Box,
  IconButton,
  InputAdornment,
  Paper,
  Popper,
  TextField,
  Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'material-react-toastify';
import { sharedColors } from '../../utils/Style';
import { getEmptySuggestions } from '../../interfaces/Suggestion/Suggestions';
import { getSuggestions } from '../../api/Suggestion';
import { ProductSummary } from '../../interfaces/Product/ProductSummary';
import { SuggestedCategory } from '../../interfaces/Suggestion/SuggestedCategory';
import { useLocalizedText } from '../../hooks/useLocalizedText';

type SearchBoxProps = {
  label?: string;
  placeholder?: string;
  width?: string | number;
  fullWidth?: boolean;
};

export const SearchBox = ({
  label,
  placeholder,
  width = 300,
  fullWidth = false,
}: SearchBoxProps) => {
  const t = useLocalizedText();

  const navigate = useNavigate();
  const location = useLocation();

  const anchorRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);

  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState(getEmptySuggestions());
  const [isFocused, setIsFocused] = useState(false);

  const blurTimeout = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (value) {
      getSuggestions(value)
        .then(setSuggestions)
        .catch((err) => toast.error(err.message));
    }
  }, [value]);

  useEffect(() => {
    setIsFocused(false);
    if (inputRef.current) {
      inputRef.current.blur();
    }
  }, [location]);

  const onSubmit = useCallback(() => {
    navigate(`/urun/ara/${value}`);
  }, [value]);

  const onClickProduct = useCallback((product: ProductSummary) => {
    navigate(`/urun/${product.slug}`);
  }, []);

  const onClickCategory = useCallback((category: SuggestedCategory) => {
    navigate(`/kategori/${category.slug}`);
  }, []);

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter') {
        e.stopPropagation();
        e.preventDefault();
        onSubmit();
      }
    },
    [onSubmit],
  );

  const hasSuggestions =
    suggestions.categories.length > 0 || suggestions.products.length > 0;

  return (
    <form autoComplete='off' onSubmit={() => {}}>
      <Box
        ref={anchorRef}
        sx={{
          width: fullWidth ? '100%' : width,
          position: 'relative',
        }}
        onFocus={() => {
          if (blurTimeout.current) clearTimeout(blurTimeout.current);
          setIsFocused(true);
        }}
        onBlur={() => {
          blurTimeout.current = setTimeout(() => setIsFocused(false), 300);
        }}
      >
        <TextField
          inputRef={inputRef}
          size='small'
          label={label}
          placeholder={placeholder}
          value={value}
          fullWidth
          sx={{
            backgroundColor: sharedColors.white,
            height: 'fit-content',
          }}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={handleKeyDown}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton
                  onClick={(ev) => {
                    ev.stopPropagation();
                    ev.preventDefault();

                    onSubmit();
                  }}
                  sx={{ p: 0, minHeight: 0, minWidth: 0 }}
                >
                  <SearchIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <Popper
          open={isFocused && hasSuggestions}
          anchorEl={anchorRef.current}
          placement='bottom-start'
        >
          <Paper
            sx={{
              display: 'flex',
              flexDirection: 'column',
              width: anchorRef.current?.offsetWidth ?? width,
              maxHeight: 300,
              overflowY: 'auto',
              zIndex: 1300,
            }}
          >
            {suggestions.products.map((product) => (
              <Box
                key={product.id}
                onClick={(ev) => {
                  ev.stopPropagation();
                  ev.preventDefault();

                  onClickProduct(product);
                }}
                sx={{
                  width: '100%',
                  px: 0.5,
                  py: 1,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: sharedColors.gray1,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: sharedColors.gray7,
                  }}
                >
                  {product.name}
                </Typography>

                <Box sx={{ display: 'flex', flexDirection: 'row', gap: 0.5 }}>
                  {product.specialPrice !== product.price && (
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: '12px',
                        color: sharedColors.gray5,
                        textDecoration: 'line-through',
                      }}
                    >
                      {product.priceFormatted}
                    </Typography>
                  )}

                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: '12px',
                      color: sharedColors.gray6,
                    }}
                  >
                    {product.specialPrice
                      ? product.specialPriceFormatted
                      : product.priceFormatted}
                  </Typography>
                </Box>
              </Box>
            ))}

            {suggestions.categories.map((category) => (
              <Box
                key={category.slug}
                onClick={(ev) => {
                  ev.stopPropagation();
                  ev.preventDefault();

                  onClickCategory(category);
                }}
                sx={{
                  width: '100%',
                  px: 0.5,
                  py: 1,
                  boxSizing: 'border-box',
                  display: 'flex',
                  flexDirection: 'column',
                  cursor: 'pointer',
                  '&:hover': {
                    backgroundColor: sharedColors.gray1,
                  },
                }}
              >
                <Typography
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    color: sharedColors.gray7,
                  }}
                >
                  {t('search_box.category', { category: category.name })}
                </Typography>

                <Typography
                  sx={{
                    fontWeight: 600,
                    fontSize: '12px',
                    color: sharedColors.gray6,
                  }}
                >
                  {t('search_box.click_to_view_products')}
                </Typography>
              </Box>
            ))}
          </Paper>
        </Popper>
      </Box>
    </form>
  );
};
